<template>
  <div>
    <NavigationBar
      titel="Abrechnung"
      :actions="actions"
      :tabs="tabs"
      :activeTab="activeTab"
    ></NavigationBar>

    <section v-if="!loading && noBillingAccountConnected">
      <v-container class="fill-height mt-16">
        <v-row align="top" justify="center">
          <v-col cols="12" sm="12" md="8">
            <v-card>
              <v-card-title class="d-flex justify-center"
                ><div class="text-center">
                  <v-avatar color="primary" rounded size="70" class="mb-4">
                    <v-icon x-large color="white">
                      mdi-credit-card-off-outline</v-icon
                    >
                  </v-avatar>
                  <div>Kein Abrechnungskonto verknüpft</div>
                </div>
              </v-card-title>
              <v-card-actions
                ><v-btn
                  @click="useExistingBillingAccount()"
                  depressed
                  class="mb-2 ml-2"
                  ><v-icon left>mdi-link-variant</v-icon>Bestehendes
                  Abrechnungskonto verwenden</v-btn
                >
                <v-spacer></v-spacer>
                <v-btn
                  @click="createNewBillingAccount()"
                  depressed
                  color="primary"
                  class="mb-2 mr-2"
                  ><v-icon left>mdi-credit-card-plus-outline</v-icon>Neues
                  Abrechnungskonto erstellen</v-btn
                ></v-card-actions
              >
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
    <section v-else-if="loading">
      <v-container class="fill-height mt-16">
        <v-row align="center" justify="center">
          <v-col cols="12" class="text-center">
            <v-progress-circular
              size="60"
              width="4"
              color="primary"
              indeterminate
            ></v-progress-circular>
            <div class="mt-4">Lade Abrechnungsdaten...</div>
          </v-col>
        </v-row>
      </v-container>
    </section>
    <section v-else>
      <v-container fluid v-if="checkIfSupport && hasWriteAccess">
        <v-btn @click="createSubscription()" disabled
          >Create Subscription</v-btn
        >
        <v-btn @click="createManualInvoice()" class="ml-2"
          >Create Manual Invoice</v-btn
        >
        <v-btn @click="linkLicensesToInvoice()" class="ml-2"
          >Link Licenses to Invoice</v-btn
        >
      </v-container>

      <v-container v-if="dataLoading" class="fill-height mt-16">
        <v-row align="center" justify="center">
          <v-col cols="12" class="text-center">
            <v-progress-circular
              size="60"
              width="4"
              color="primary"
              indeterminate
            ></v-progress-circular>
            <div class="mt-4">Lade Abrechnungsdaten...</div>
          </v-col>
        </v-row>
      </v-container>

      <router-view
        v-else
        :customer="customer"
        :subscriptions="subscriptions"
        :licenses="licenses"
        :invoices="invoices"
        :hasWriteAccess="hasWriteAccess"
        @open-portal="toggleCreateStripePortalSessionDialog"
      >
      </router-view>
    </section>
    <section>
      <CustomDialog
        v-if="createStripePortalSessionDialog.model"
        @close="toggleCreateStripePortalSessionDialog()"
        title="Abrechnungsportal öffnen"
        text="Im Abrechnungsportal können Kundendaten bearbeitet werden sowie ebenfalls Rechnungen und Zahlungsbelege heruntergeladen werden. Dazu verlässt Du diese Seite und wirst zu unserem Abrechnungsdienstleister Stripe weitergeleitet."
        :loading="createStripePortalSessionDialog.loading"
        :btnAction="createStripePortalSession"
        :errorMessage="createStripePortalSessionDialog.errorMessage"
        width="s"
      ></CustomDialog>
    </section>

    <!-- Link Licenses Dialog -->
    <section>
      <v-dialog v-model="linkLicensesDialog.model" fullscreen persistent>
        <v-card class="d-flex flex-column" style="height: 100vh">
          <v-card-title class="headline d-flex align-center">
            Lizenzen mit Rechnung verknüpfen
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="closeLinkLicensesDialog"
              :disabled="linkLicensesDialog.loading"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="flex-grow-1 overflow-hidden">
            <!-- Main Content Area -->
            <v-row class="fill-height">
              <!-- General Settings Column -->
              <v-col cols="12" md="3">
                <div class="subtitle-1 font-weight-bold mb-3">
                  Einstellungen
                </div>

                <!-- Invoice Selection -->
                <v-autocomplete
                  v-model="linkLicensesDialog.invoiceId"
                  :items="availableInvoices"
                  item-text="displayText"
                  item-value="id"
                  label="Rechnung"
                  placeholder="Rechnung auswählen"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="mb-3"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        Rechnung
                        <code>{{ item.number || item.id }}</code>
                        vom {{ timestampConverter(item.created) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <strong>{{
                          getAmountFromCents(item.total, item.currency)
                        }}</strong>
                        <span
                          v-if="item.period_start && item.period_end"
                          class="ml-2 grey--text"
                        >
                          |
                          {{
                            new Date(
                              item.period_start * 1000,
                            ).toLocaleDateString("de-DE")
                          }}
                          -
                          {{
                            new Date(item.period_end * 1000).toLocaleDateString(
                              "de-DE",
                            )
                          }}
                        </span>
                        <span class="ml-2 grey--text">| {{ item.id }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <invoice-status-chip
                        small
                        :status="item.status"
                        class="ml-2"
                      >
                      </invoice-status-chip>
                    </v-list-item-action>
                  </template>
                </v-autocomplete>

                <!-- Line Item Selection -->
                <v-autocomplete
                  ref="lineItemSelect"
                  v-model="linkLicensesDialog.currentLicense.lineItemId"
                  :items="availableLineItems"
                  item-text="displayText"
                  item-value="id"
                  label="Line Item"
                  outlined
                  dense
                  hide-details
                  :disabled="
                    !linkLicensesDialog.invoiceId ||
                    availableLineItems.length === 0
                  "
                  :placeholder="
                    !linkLicensesDialog.invoiceId
                      ? 'Zuerst Invoice ID eingeben'
                      : availableLineItems.length === 0
                      ? 'Keine Line Items gefunden'
                      : 'Line Item auswählen'
                  "
                  @change="onLineItemSelected"
                  class="mb-3"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.description }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <strong>{{
                          getAmountFromCents(item.amount, item.currency)
                        }}</strong>
                        <span v-if="item.quantity > 1" class="ml-1">
                          ({{ item.quantity }}x)
                        </span>
                        <span v-if="item.period" class="ml-2 grey--text">
                          | {{ item.period }}
                        </span>
                        <span class="ml-2 grey--text">ID: {{ item.id }}</span>
                        <v-chip
                          v-if="item.isProration"
                          x-small
                          color="orange"
                          class="ml-2"
                          outlined
                        >
                          Anteilig{{
                            item.extractedQuantity
                              ? ` (${item.extractedQuantity}x)`
                              : ""
                          }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:append-outer>
                    <v-chip
                      v-if="selectedLineItemQuantity > 1"
                      x-small
                      color="primary"
                      outlined
                    >
                      {{ linkLicensesDialog.currentLicense.userIds.length }}/{{
                        selectedLineItemQuantity
                      }}
                    </v-chip>
                  </template>
                </v-autocomplete>

                <!-- Proration Settings -->
                <v-divider class="my-3"></v-divider>
                <div class="subtitle-2 font-weight-bold mb-2">
                  Anteilige Abrechnung (Proration)
                </div>

                <v-checkbox
                  v-model="linkLicensesDialog.currentLicense.prorated"
                  label="Anteilig abgerechnet"
                  dense
                  hide-details
                  class="mt-0 mb-2"
                  :disabled="!linkLicensesDialog.currentLicense.lineItemId"
                ></v-checkbox>

                <v-text-field
                  v-if="linkLicensesDialog.currentLicense.prorated"
                  v-model.number="
                    linkLicensesDialog.currentLicense.sharedQuantity
                  "
                  label="Anzahl Nutzer (teilen sich den Betrag)"
                  type="number"
                  step="1"
                  min="1"
                  outlined
                  dense
                  class="mb-2"
                  :disabled="!linkLicensesDialog.currentLicense.lineItemId"
                  placeholder="z.B. 4"
                  hint="Anzahl der Nutzer, die sich diesen Line Item teilen"
                  persistent-hint
                >
                  <template v-slot:append>
                    <span
                      v-if="selectedLineItemForProration"
                      class="text-caption grey--text"
                    >
                      =
                      {{
                        getAmountFromCents(
                          Math.round(
                            selectedLineItemForProration.amount /
                              (linkLicensesDialog.currentLicense
                                .sharedQuantity || 1),
                          ),
                          selectedLineItemForProration.currency,
                        )
                      }}/Person
                    </span>
                  </template>
                </v-text-field>

                <v-alert
                  v-if="
                    linkLicensesDialog.currentLicense.prorated &&
                    selectedLineItemForProration
                  "
                  type="info"
                  dense
                  text
                  class="mb-2"
                >
                  <strong>{{ selectedLineItemForProration.description }}</strong
                  ><br />
                  Gesamtbetrag:
                  {{
                    getAmountFromCents(
                      selectedLineItemForProration.amount,
                      selectedLineItemForProration.currency,
                    )
                  }}
                  ÷
                  {{ linkLicensesDialog.currentLicense.sharedQuantity || "?" }}
                  Nutzer =
                  <strong>{{
                    getAmountFromCents(
                      Math.round(
                        selectedLineItemForProration.amount /
                          (linkLicensesDialog.currentLicense.sharedQuantity ||
                            1),
                      ),
                      selectedLineItemForProration.currency,
                    )
                  }}</strong>
                  pro Person
                </v-alert>

                <v-divider class="my-3"></v-divider>

                <!-- License/Product Selection -->
                <div class="subtitle-2 font-weight-bold mb-2">
                  Lizenz/Produkt Zuordnung
                  <v-chip
                    v-if="linkLicensesDialog.currentLicense.prorated"
                    x-small
                    color="error"
                    class="ml-2"
                  >
                    Erforderlich
                  </v-chip>
                </div>

                <v-autocomplete
                  v-model="linkLicensesDialog.currentLicense.selectedLicense"
                  :items="products"
                  item-text="name"
                  item-value="id"
                  label="Lizenz/Produkt"
                  outlined
                  dense
                  clearable
                  :disabled="!linkLicensesDialog.currentLicense.lineItemId"
                  :placeholder="
                    !linkLicensesDialog.currentLicense.lineItemId
                      ? 'Zuerst Line Item auswählen'
                      : 'Lizenz/Produkt auswählen'
                  "
                  :rules="
                    linkLicensesDialog.currentLicense.prorated
                      ? [
                          (v) =>
                            !!v || 'Für anteilige Abrechnungen erforderlich',
                        ]
                      : []
                  "
                  class="mb-2"
                  hint="Wählen Sie das zugehörige Produkt für saubere Anzeige"
                  persistent-hint
                >
                  <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                      <v-icon left small color="primary">mdi-license</v-icon>
                      <span>{{ item.name }}</span>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon left small>mdi-license</v-icon>
                        {{ item.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="item.description">
                        {{ item.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <v-alert
                  v-if="linkLicensesDialog.currentLicense.selectedLicense"
                  type="success"
                  dense
                  text
                  class="mb-2"
                >
                  <strong>{{
                    products.find(
                      (p) =>
                        p.id ===
                        linkLicensesDialog.currentLicense.selectedLicense,
                    )?.name
                  }}</strong>
                  wird für Anzeige verwendet
                </v-alert>

                <v-divider class="my-3"></v-divider>

                <!-- Reference Invoice Selection for Pre-filling -->
                <div class="subtitle-2 font-weight-bold mb-2">
                  Vorausfüllen aus vorheriger Rechnung
                </div>

                <v-autocomplete
                  v-model="linkLicensesDialog.referenceInvoiceId"
                  :items="availableInvoices"
                  item-text="displayText"
                  item-value="id"
                  label="Referenz-Rechnung"
                  placeholder="Optional: Vorherige Rechnung"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="mb-2"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        Rechnung
                        <code>{{ item.number || item.id }}</code>
                        vom {{ timestampConverter(item.created) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <strong>{{
                          getAmountFromCents(item.total, item.currency)
                        }}</strong>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <v-autocomplete
                  v-model="linkLicensesDialog.referenceLineItemId"
                  :items="availableReferenceLineItems"
                  item-text="displayText"
                  item-value="id"
                  label="Referenz Line Item"
                  outlined
                  dense
                  hide-details
                  :disabled="!linkLicensesDialog.referenceInvoiceId"
                  :placeholder="
                    !linkLicensesDialog.referenceInvoiceId
                      ? 'Zuerst Referenz-Rechnung wählen'
                      : 'Line Item auswählen'
                  "
                  clearable
                  class="mb-2"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.description }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <strong>{{
                          getAmountFromCents(item.amount, item.currency)
                        }}</strong>
                        <span v-if="item.quantity > 1" class="ml-1">
                          ({{ item.quantity }}x)
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>

                <v-btn
                  v-if="
                    linkLicensesDialog.referenceLineItemId &&
                    linkLicensesDialog.currentLicense.lineItemId
                  "
                  color="primary"
                  outlined
                  block
                  small
                  @click="loadUsersFromReferenceLineItem"
                  class="mb-3"
                >
                  <v-icon left small>mdi-account-multiple-plus</v-icon>
                  Benutzer aus Referenz übernehmen
                </v-btn>

                <v-divider class="my-3"></v-divider>

                <!-- Division Filter -->
                <v-select
                  v-model="linkLicensesDialog.divisionFilter"
                  :items="divisions"
                  item-text="title"
                  item-value="id"
                  label="Filter nach Abteilung"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-filter"
                  class="mb-2"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.title }}
                  </template>
                </v-select>

                <!-- Grouping Toggle -->
                <v-switch
                  v-model="linkLicensesDialog.groupByDivision"
                  label="Nach Abteilung gruppieren"
                  dense
                  hide-details
                  class="mb-2"
                ></v-switch>

                <v-divider class="my-3"></v-divider>

                <!-- Quantity helper info -->
                <v-alert
                  v-if="
                    selectedLineItemQuantity > 1 &&
                    linkLicensesDialog.currentLicense.lineItemId &&
                    linkLicensesDialog.currentLicense.userIds.length === 0
                  "
                  type="info"
                  dense
                  text
                  class="mb-3"
                >
                  Line Item Menge:
                  <strong>{{ selectedLineItemQuantity }}</strong>
                </v-alert>

                <!-- Success feedback -->
                <v-alert
                  v-if="
                    linkLicensesDialog.currentLicense.userIds.length > 0 &&
                    linkLicensesDialog.currentLicense.userIds.length ===
                      selectedLineItemQuantity
                  "
                  type="success"
                  dense
                  text
                  class="mb-3"
                >
                  Perfekt!
                  {{ linkLicensesDialog.currentLicense.userIds.length }}
                  Benutzer ausgewählt.
                </v-alert>

                <!-- Manual Add Button -->
                <v-btn
                  v-if="
                    linkLicensesDialog.currentLicense.userIds.length > 0 &&
                    linkLicensesDialog.currentLicense.lineItemId
                  "
                  color="primary"
                  depressed
                  block
                  @click="addLicensesToListManually"
                  :disabled="!linkLicensesDialog.currentLicense.lineItemId"
                  class="mb-3"
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ linkLicensesDialog.currentLicense.userIds.length }}
                  {{
                    linkLicensesDialog.currentLicense.userIds.length === 1
                      ? "Lizenz"
                      : "Lizenzen"
                  }}
                  hinzufügen
                </v-btn>
              </v-col>

              <v-col cols="12" md="3">
                <div class="subtitle-1 font-weight-bold mb-3">
                  Aktuelle Auswahl
                </div>

                <!-- Current Selection Info -->
                <div class="subtitle-2 font-weight-bold mb-2">
                  Aktuelle Auswahl ({{
                    linkLicensesDialog.currentLicense.userIds.length
                  }})
                </div>

                <v-card
                  outlined
                  class="mb-3"
                  style="max-height: 380px; overflow-y: auto"
                >
                  <v-alert
                    v-if="
                      linkLicensesDialog.currentLicense.userIds.length === 0
                    "
                    type="info"
                    outlined
                    dense
                    text
                    class="ma-2"
                  >
                    Keine Benutzer ausgewählt.
                  </v-alert>

                  <v-list v-else dense>
                    <v-list-item
                      v-for="userId in linkLicensesDialog.currentLicense
                        .userIds"
                      :key="userId"
                      dense
                      class="py-0"
                    >
                      <v-list-item-content class="my-0 py-0">
                        <v-list-item-title class="text-body-2">
                          {{ getUserDisplayName(userId) }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-btn
                          icon
                          x-small
                          @click="toggleUserSelection(userId)"
                          class="pa-0"
                        >
                          <v-icon x-small class="pa-0">mdi-close</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>

              <!-- User Selection List -->
              <v-col cols="12" md="3">
                <div class="subtitle-1 font-weight-bold">
                  Benutzer auswählen
                </div>

                <v-text-field
                  v-model="linkLicensesDialog.userSearch"
                  dense
                  outlined
                  hide-details
                  placeholder="Suchen..."
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  style="max-width: 250px"
                  class="mb-3"
                ></v-text-field>

                <v-card outlined style="max-height: 75vh; overflow-y: auto">
                  <v-progress-linear
                    v-if="linkLicensesDialog.loadingMemberships"
                    indeterminate
                    color="primary"
                  ></v-progress-linear>

                  <v-list
                    v-if="!linkLicensesDialog.loadingMemberships"
                    dense
                    class="overflow-y-auto flex-grow-1"
                    style="min-height: 0"
                    nav
                  >
                    <!-- User list with optional grouping -->
                    <template
                      v-for="(group, groupIndex) in groupedAvailableMembers"
                    >
                      <!-- Division header -->
                      <v-subheader
                        v-if="
                          linkLicensesDialog.groupByDivision && group.division
                        "
                        :key="'div-' + groupIndex"
                        class="font-weight-bold"
                      >
                        <v-icon small left>mdi-domain</v-icon>
                        {{ group.division.title }}
                        <v-spacer></v-spacer>
                        <v-chip x-small outlined>
                          {{ group.members.length }}
                        </v-chip>
                      </v-subheader>

                      <!-- Members in this group -->
                      <v-list-item
                        v-for="member in group.members"
                        :key="member.userId"
                        @click="toggleUserSelection(member.userId)"
                        :disabled="
                          !linkLicensesDialog.currentLicense.lineItemId
                        "
                        :class="{
                          'blue lighten-5':
                            linkLicensesDialog.currentLicense.userIds.includes(
                              member.userId,
                            ),
                          'orange lighten-5': member.isInCurrentLineItem,
                        }"
                        ripple
                      >
                        <v-list-item-action>
                          <v-checkbox
                            :input-value="
                              linkLicensesDialog.currentLicense.userIds.includes(
                                member.userId,
                              )
                            "
                            :disabled="
                              !linkLicensesDialog.currentLicense.lineItemId
                            "
                            color="primary"
                            @click.stop="toggleUserSelection(member.userId)"
                          ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ member.displayName }}
                            <v-chip
                              v-if="
                                !linkLicensesDialog.groupByDivision &&
                                member.division?.title
                              "
                              x-small
                              outlined
                              class="ml-2"
                            >
                              {{ member.division.title }}
                            </v-chip>
                            <v-chip
                              v-if="member.isInCurrentLineItem"
                              x-small
                              color="orange"
                              text-color="white"
                              class="ml-2"
                            >
                              Bereits hinzugefügt
                            </v-chip>
                            <v-chip
                              v-else-if="member.isAssignedToOtherLineItems"
                              x-small
                              color="grey"
                              outlined
                              class="ml-2"
                            >
                              In anderem Line Item
                            </v-chip>
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="member.personnelNumber">
                            PN: {{ member.personnelNumber }}
                            <span
                              v-if="getUserCurrentLicense(member.userId)"
                              class="ml-2"
                            >
                              | Lizenz:
                              {{ getUserCurrentLicense(member.userId) }}
                            </span>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle
                            v-else-if="getUserCurrentLicense(member.userId)"
                          >
                            Lizenz: {{ getUserCurrentLicense(member.userId) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>

                    <v-list-item v-if="filteredAvailableMembers.length === 0">
                      <v-list-item-content>
                        <v-list-item-title class="text-center grey--text">
                          {{
                            linkLicensesDialog.userSearch ||
                            linkLicensesDialog.divisionFilter
                              ? "Keine Benutzer gefunden"
                              : linkLicensesDialog.currentLicense.lineItemId
                              ? "Keine Benutzer verfügbar"
                              : "Wähle zuerst ein Line Item aus"
                          }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>

              <!-- Added Licenses List -->
              <v-col
                cols="12"
                md="3"
                class="d-flex flex-column"
                style="height: 100%"
              >
                <div class="subtitle-1 font-weight-bold mb-3">
                  Hinzugefügte Lizenzen ({{
                    linkLicensesDialog.licenses.length
                  }})
                </div>

                <v-card
                  outlined
                  class="flex-grow-1 d-flex flex-column"
                  style="min-height: 0"
                >
                  <v-alert
                    v-if="linkLicensesDialog.licenses.length === 0"
                    type="info"
                    outlined
                    dense
                    class="ma-4"
                  >
                    Keine Lizenzen hinzugefügt. Wähle Benutzer aus der Liste
                    links aus.
                  </v-alert>

                  <v-list v-else dense>
                    <div
                      v-for="(group, lineItemId) in groupedLicenses"
                      :key="lineItemId"
                    >
                      <v-subheader class="font-weight-bold primary--text">
                        {{ getLineItemDescription(lineItemId) }}
                        <v-spacer></v-spacer>
                        <v-chip x-small>{{ group.length }}</v-chip>
                      </v-subheader>
                      <v-list-item
                        v-for="(license, index) in group"
                        :key="'license-' + index"
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ getUserDisplayName(license.userId) }}
                          </v-list-item-title>
                          <v-list-item-subtitle
                            v-if="getUserCurrentLicense(license.userId)"
                          >
                            Aktuelle Lizenz:
                            {{ getUserCurrentLicense(license.userId) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            icon
                            small
                            @click="
                              removeLicenseFromList(license.originalIndex)
                            "
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider class="my-2"></v-divider>
                    </div>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions v-if="linkLicensesDialog.errorMessage" class="pt-0">
            <v-alert type="error" dense text class="mb-0 flex-grow-1">
              {{ linkLicensesDialog.errorMessage }}
            </v-alert>
          </v-card-actions>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="closeLinkLicensesDialog"
              :disabled="linkLicensesDialog.loading"
            >
              Abbrechen
            </v-btn>
            <v-btn
              color="primary"
              depressed
              @click="executeLinkLicensesToInvoice"
              :loading="linkLicensesDialog.loading"
              :disabled="
                !linkLicensesDialog.invoiceId ||
                linkLicensesDialog.licenses.length === 0
              "
            >
              Verknüpfen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </section>
  </div>
</template>

<script>
import { db, functions, auth } from "@/firebase";
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
import InvoiceStatusChip from "@/components/admin/billing/InvoiceStatusChip.vue";
// import VueApexCharts from "vue-apexcharts";
import { ADMIN } from "@/store/modules.js";
import { GET_USER_LIST } from "@/store/action-types.js";
import { ADMINISTRATION_BILLING_read } from "@/data/permission-types.js";
import { billingHelpersMixin } from "@/mixins/billingHelpers";
import {
  USE_DEV_ENVIRONMENT,
  ENV_CONFIG,
  INVOICE_CONFIG,
  LINE_ITEMS_CONFIG,
} from "@/config/invoiceConfig";

export default {
  name: "admin-billing",
  components: {
    NavigationBar,
    InvoiceStatusChip,
    CustomDialog,
    // VueApexCharts,
  },
  mixins: [billingHelpersMixin],
  data() {
    return {
      activeTab: `overview`,
      tabs: [
        {
          id: 1,
          name: "Übersicht",
          route: `overview`,
          permission: `${ADMINISTRATION_BILLING_read}`,
        },
        {
          id: 2,
          name: "Abonnements",
          route: `subscriptions`,
          permission: `${ADMINISTRATION_BILLING_read}`,
        },
        {
          id: 3,
          name: "Rechnungen",
          route: `invoices`,
          permission: `${ADMINISTRATION_BILLING_read}`,
        },
        {
          id: 4,
          name: "Lizenzen",
          route: `licenses`,
          permission: `${ADMINISTRATION_BILLING_read}`,
        },
      ],

      billingPortalAction: {
        title: "Abrechnungsportal öffnen",
        icon: "mdi-open-in-new",
        actionStyle: "textBtnSubmit",
        color: "primary",
        function: this.toggleCreateStripePortalSessionDialog,
      },
      createStripePortalSessionDialog: {
        model: false,
        loading: false,
        errorMessage: "",
      },
      linkLicensesDialog: {
        model: false,
        loading: false,
        loadingMemberships: false,
        errorMessage: "",
        invoiceId: "",
        licenses: [],
        currentLicense: {
          userIds: [],
          lineItemId: "",
          prorated: false,
          sharedQuantity: 1,
          selectedLicense: null, // Selected license/product for manual selection
        },
        referenceInvoiceId: "",
        referenceLineItemId: "",
        userSearch: "",
        divisionFilter: null,
        groupByDivision: true,
      },
      loading: true,
      customer: null,
      dataLoading: false,
      subscriptions: [],
      licenses: [],
      invoices: [],
      products: [], // Available products for license selection
      currentUserId: auth.currentUser ? auth.currentUser.uid : null,
    };
  },
  computed: {
    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    hasReadAccess() {
      if (!this.customer || !this.currentUserId) return false;
      const readAccessUserIds = this.customer.readAccessUserIds || [];
      const writeAccessUserIds = this.customer.writeAccessUserIds || [];
      return (
        readAccessUserIds.includes(this.currentUserId) ||
        writeAccessUserIds.includes(this.currentUserId)
      );
    },
    hasWriteAccess() {
      if (!this.customer || !this.currentUserId) return false;
      const writeAccessUserIds = this.customer.writeAccessUserIds || [];
      return writeAccessUserIds.includes(this.currentUserId);
    },
    organizationBillingConfig() {
      return this.$store.state.organization.activeOrganization?.billing || {};
    },
    noBillingAccountConnected() {
      return !this.organizationBillingConfig.customerId;
    },
    actions() {
      if (this.hasWriteAccess) {
        return [this.billingPortalAction];
      } else return [];
    },
    memberships() {
      return this.$store.state.admin.membershipsPublic || [];
    },
    divisions() {
      // Extract unique divisions from memberships
      const divisionMap = new Map();
      this.memberships.forEach((member) => {
        if (member.user && member.user.division && member.user.division.id) {
          const div = member.user.division;
          if (!divisionMap.has(div.id)) {
            divisionMap.set(div.id, {
              id: div.id,
              title: div.title || "Unbekannte Abteilung",
              shortTitle: div.shortTitle,
              sortKey: div.sortKey || 999,
            });
          }
        }
      });
      return Array.from(divisionMap.values()).sort(
        (a, b) => (a.sortKey || 999) - (b.sortKey || 999),
      );
    },
    availableInvoices() {
      return this.invoices.map((invoice) => ({
        id: invoice.id,
        created: invoice.created,
        number: invoice.number,
        status: invoice.status,
        total: invoice.total,
        currency: invoice.currency,
        period_start: invoice.period_start,
        period_end: invoice.period_end,
        displayText: `${
          invoice.number || invoice.id
        } - ${this.getAmountFromCents(invoice.total, invoice.currency)} (${
          invoice.status
        })`,
      }));
    },
    selectedInvoice() {
      if (!this.linkLicensesDialog.invoiceId) return null;
      return this.invoices.find(
        (inv) => inv.id === this.linkLicensesDialog.invoiceId,
      );
    },
    referenceInvoice() {
      if (!this.linkLicensesDialog.referenceInvoiceId) return null;
      return this.invoices.find(
        (inv) => inv.id === this.linkLicensesDialog.referenceInvoiceId,
      );
    },
    availableLineItems() {
      if (!this.selectedInvoice) {
        return [];
      }

      // Get line items from invoice.lines array
      const lineItems = Array.isArray(this.selectedInvoice.lines)
        ? this.selectedInvoice.lines
        : [];

      if (lineItems.length === 0) {
        return [];
      }

      // Get invoice period from invoice level
      let invoicePeriod = null;
      if (
        this.selectedInvoice.period_start &&
        this.selectedInvoice.period_end
      ) {
        invoicePeriod = `${new Date(
          this.selectedInvoice.period_start * 1000,
        ).toLocaleDateString("de-DE")} - ${new Date(
          this.selectedInvoice.period_end * 1000,
        ).toLocaleDateString("de-DE")}`;
      }

      return lineItems.map((line) => {
        const quantity = line.quantity || 1;

        // Check description for proration keywords (German)
        const description = line.description || "";
        const descLower = description.toLowerCase();
        const hasProrationKeywords =
          descLower.includes("unbenutzte zeit") ||
          descLower.includes("ungenutzte zeit") ||
          descLower.includes("verbleibende zeit") ||
          descLower.includes("anteilig") ||
          descLower.includes("unused time") ||
          descLower.includes("remaining time") ||
          line.proration === true;

        // Try to extract quantity from description (e.g., "für 4 x Mira One" -> 4)
        let extractedQuantity = null;
        const quantityMatch =
          description.match(/für\s+(\d+)\s*x/i) ||
          description.match(/for\s+(\d+)\s*x/i) ||
          description.match(/(\d+)\s*×/i);
        if (quantityMatch) {
          extractedQuantity = parseInt(quantityMatch[1], 10);
        }

        return {
          id: line.id,
          description: description || "Keine Beschreibung",
          amount: line.amount,
          currency: line.currency,
          quantity: quantity,
          period: invoicePeriod,
          isProration: hasProrationKeywords,
          extractedQuantity: extractedQuantity,
          rawLine: line,
          displayText: `${
            description || "Keine Beschreibung"
          } - ${this.getAmountFromCents(line.amount, line.currency)}${
            quantity > 1 ? ` (${quantity}x)` : ""
          }`,
        };
      });
    },
    availableReferenceLineItems() {
      if (!this.referenceInvoice) {
        return [];
      }

      // Get line items from reference invoice
      const lineItems = Array.isArray(this.referenceInvoice.lines)
        ? this.referenceInvoice.lines
        : [];

      if (lineItems.length === 0) {
        return [];
      }

      // Get invoice period from invoice level
      let invoicePeriod = null;
      if (
        this.referenceInvoice.period_start &&
        this.referenceInvoice.period_end
      ) {
        invoicePeriod = `${new Date(
          this.referenceInvoice.period_start * 1000,
        ).toLocaleDateString("de-DE")} - ${new Date(
          this.referenceInvoice.period_end * 1000,
        ).toLocaleDateString("de-DE")}`;
      }

      return lineItems.map((line) => ({
        id: line.id,
        description: line.description || "Keine Beschreibung",
        amount: line.amount,
        currency: line.currency,
        quantity: line.quantity || 1,
        period: invoicePeriod,
        displayText: `${
          line.description || "Keine Beschreibung"
        } - ${this.getAmountFromCents(line.amount, line.currency)}${
          line.quantity > 1 ? ` (${line.quantity}x)` : ""
        }`,
      }));
    },
    availableMembers() {
      // Get users from Vuex admin store
      const userList = this.memberships || [];

      // Get users already assigned to current line item
      const currentLineItemId =
        this.linkLicensesDialog.currentLicense.lineItemId;
      const currentLineItemUserIds = this.linkLicensesDialog.licenses
        .filter((license) => license.lineItemId === currentLineItemId)
        .map((license) => license.userId);

      // Get all assigned users with their line items
      const assignedUsersMap = {};
      this.linkLicensesDialog.licenses.forEach((license) => {
        if (!assignedUsersMap[license.userId]) {
          assignedUsersMap[license.userId] = [];
        }
        assignedUsersMap[license.userId].push(license.lineItemId);
      });

      return userList.map((item) => {
        const userId = item.user.uid;
        const isInCurrentLineItem = currentLineItemUserIds.includes(userId);
        const assignedLineItems = assignedUsersMap[userId] || [];
        const isAssignedToOtherLineItems = assignedLineItems.some(
          (lineItemId) => lineItemId !== currentLineItemId,
        );

        return {
          userId: userId,
          displayName: item.user.displayName || "Unbekannt",
          personnelNumber: item.user.personnelNumber,
          division: item.user.division || {
            id: null,
            title: "Keine Abteilung",
          },
          displayText: `${item.user.displayName || "Unbekannt"}${
            item.user.personnelNumber ? ` (${item.user.personnelNumber})` : ""
          }`,
          disabled: false, // Never disable, just show visual indicator
          isInCurrentLineItem: isInCurrentLineItem,
          isAssignedToOtherLineItems: isAssignedToOtherLineItems,
          assignedLineItems: assignedLineItems,
        };
      });
    },
    filteredAvailableMembers() {
      const search = (this.linkLicensesDialog.userSearch || "").toLowerCase();
      let filtered = this.availableMembers;

      // Apply division filter
      if (this.linkLicensesDialog.divisionFilter) {
        filtered = filtered.filter(
          (member) =>
            member.division?.id === this.linkLicensesDialog.divisionFilter,
        );
      }

      // Apply search filter
      if (search) {
        filtered = filtered.filter((member) => {
          return (
            member.displayName.toLowerCase().includes(search) ||
            (member.personnelNumber &&
              member.personnelNumber.toLowerCase().includes(search)) ||
            (member.division?.title &&
              member.division.title.toLowerCase().includes(search))
          );
        });
      }

      return filtered;
    },
    groupedAvailableMembers() {
      if (!this.linkLicensesDialog.groupByDivision) {
        return [{ division: null, members: this.filteredAvailableMembers }];
      }

      const grouped = {};
      this.filteredAvailableMembers.forEach((member) => {
        const divisionId = member.division?.id || "no-division";
        if (!grouped[divisionId]) {
          grouped[divisionId] = {
            division: member.division || {
              id: "no-division",
              title: "Keine Abteilung",
              sortKey: 9999,
            },
            members: [],
          };
        }
        grouped[divisionId].members.push(member);
      });

      return Object.values(grouped).sort(
        (a, b) => (a.division.sortKey || 9999) - (b.division.sortKey || 9999),
      );
    },
    groupedLicenses() {
      const grouped = {};
      this.linkLicensesDialog.licenses.forEach((license, index) => {
        const lineItemId = license.lineItemId;
        if (!grouped[lineItemId]) {
          grouped[lineItemId] = [];
        }
        grouped[lineItemId].push({
          ...license,
          originalIndex: index,
        });
      });
      return grouped;
    },
    selectedLineItemQuantity() {
      if (!this.linkLicensesDialog.currentLicense.lineItemId) return 1;
      const lineItem = this.availableLineItems.find(
        (item) => item.id === this.linkLicensesDialog.currentLicense.lineItemId,
      );
      return lineItem?.quantity || 1;
    },
    selectedLineItemForProration() {
      if (!this.linkLicensesDialog.currentLicense.lineItemId) return null;
      return this.availableLineItems.find(
        (item) => item.id === this.linkLicensesDialog.currentLicense.lineItemId,
      );
    },
  },
  async created() {
    if (this.organizationBillingConfig.customerId) {
      try {
        await this.getcustomer();
        await this.fetchAllData();
      } catch (error) {
        console.error("Error loading customer data:", error);
      }
    }
    this.loading = false;
  },
  methods: {
    async fetchAllData() {
      this.dataLoading = true;
      try {
        await Promise.all([
          this.getInvoices(),
          this.getLicenses(),
          this.getSubscriptions(),
          this.getProducts(),
        ]);
      } catch (error) {
        console.error("Error fetching billing data:", error);
        this.$store.commit("snackbar/setSnackbar", {
          type: "error",
          message: "Fehler beim Laden der Abrechnungsdaten",
        });
      } finally {
        this.dataLoading = false;
      }
    },
    async getcustomer() {
      try {
        const customerId = this.organizationBillingConfig.customerId;
        const customerDoc = await db
          .collection("customers")
          .doc(customerId)
          .get();
        this.customer = customerDoc.data();
      } catch (error) {
        throw new Error("Failed to get customer data: " + error);
      }
    },
    async getInvoices() {
      try {
        const customerId = this.organizationBillingConfig.customerId;
        const invoicesSnapshot = await db
          .collection("customers")
          .doc(customerId)
          .collection("invoices")
          .where("status", "in", ["open", "paid", "uncollectible", "void"])
          .get();

        // Get all invoices
        const invoices = invoicesSnapshot.docs.map((doc) => {
          return doc.data();
        });

        // Enrich invoices with license data
        const licensesSnapshot = await db
          .collection(`customers/${customerId}/licenses`)
          .get();

        const licensesByInvoice = {};

        licensesSnapshot.docs.forEach((doc) => {
          const license = doc.data();

          // Check billing history for this license
          if (license.billingHistory && Array.isArray(license.billingHistory)) {
            license.billingHistory.forEach((billing) => {
              const invoiceId = billing.invoiceId;
              if (invoiceId) {
                if (!licensesByInvoice[invoiceId]) {
                  licensesByInvoice[invoiceId] = {
                    licenses: [],
                    totalLicenses: 0,
                    licenseSummary: {},
                  };
                }

                // Use historical product name from billing entry, not current license name
                const licenseType =
                  billing.productName || license.name || "Unbekannt";

                // Calculate proration details from billing entry
                let prorationDetails = {
                  prorationFactor: 1,
                  activeDays: 0,
                  fullPeriodDays: 0,
                  prorationReason: "full_period",
                };

                // Check if this entry is marked as prorated in billing history
                if (billing.prorated) {
                  // Use the prorationFactor from billing entry if available and valid
                  if (
                    billing.prorationFactor &&
                    billing.prorationFactor > 0 &&
                    billing.prorationFactor !== 1
                  ) {
                    prorationDetails.prorationFactor = billing.prorationFactor;
                  } else if (
                    billing.unitAmount &&
                    billing.amount &&
                    billing.unitAmount > 0
                  ) {
                    // Calculate from amounts if factor not available
                    prorationDetails.prorationFactor =
                      billing.amount / billing.unitAmount;
                  }
                  prorationDetails.prorationReason = "prorated";
                }

                if (billing.periodStart && billing.periodEnd) {
                  const periodStart = billing.periodStart.toDate();
                  const periodEnd = billing.periodEnd.toDate();
                  const fullPeriodDays = Math.ceil(
                    (periodEnd - periodStart) / (1000 * 60 * 60 * 24),
                  );

                  // Check if license was prorated based on invoice period
                  const invoice = invoices.find((inv) => inv.id === invoiceId);
                  if (invoice && invoice.period_start && invoice.period_end) {
                    const invoicePeriodStart = new Date(
                      invoice.period_start * 1000,
                    );
                    const invoicePeriodEnd = new Date(
                      invoice.period_end * 1000,
                    );

                    const invoiceDays = Math.ceil(
                      (invoicePeriodEnd - invoicePeriodStart) /
                        (1000 * 60 * 60 * 24),
                    );

                    if (periodStart > invoicePeriodStart) {
                      prorationDetails.prorationReason = "started_mid_period";
                    } else if (periodEnd < invoicePeriodEnd) {
                      prorationDetails.prorationReason = "ended_mid_period";
                    }

                    prorationDetails.activeDays = fullPeriodDays;
                    prorationDetails.fullPeriodDays = invoiceDays;

                    // Only recalculate if not already set from billing entry
                    if (
                      prorationDetails.prorationFactor === 1 &&
                      billing.prorated
                    ) {
                      prorationDetails.prorationFactor =
                        fullPeriodDays / invoiceDays;
                    }
                  } else {
                    prorationDetails.activeDays = fullPeriodDays;
                    prorationDetails.fullPeriodDays = fullPeriodDays;
                  }
                }

                // Use unit amount from billing entry (historical price)
                const unitAmount = billing.unitAmount || billing.amount || 0;

                licensesByInvoice[invoiceId].licenses.push({
                  licenseId: license.licenseId,
                  userName: license.user?.displayName || "Unbekannt",
                  userEmail: license.user?.email,
                  userId: license.userId,
                  lineItemId: billing.lineItemId || null,
                  licenseType: licenseType, // Historical product name
                  status: billing.status || license.status || "active", // Historical status from billing entry
                  billedAmount: billing.amount,
                  unitAmount: unitAmount,
                  currency: billing.currency || "eur",
                  periodStart: billing.periodStart,
                  periodEnd: billing.periodEnd,
                  prorationDetails: prorationDetails,
                  sharedQuantity: billing.sharedQuantity || null, // Number of users sharing this line item
                });

                licensesByInvoice[invoiceId].totalLicenses++;

                if (!licensesByInvoice[invoiceId].licenseSummary[licenseType]) {
                  licensesByInvoice[invoiceId].licenseSummary[licenseType] = {
                    count: 0,
                    totalAmount: 0,
                  };
                }

                licensesByInvoice[invoiceId].licenseSummary[licenseType]
                  .count++;
                licensesByInvoice[invoiceId].licenseSummary[
                  licenseType
                ].totalAmount += billing.amount || 0;
              }
            });
          }
        });

        // Merge license data into invoices
        this.invoices = invoices.map((invoice) => {
          const invoiceData = licensesByInvoice[invoice.id];
          return {
            ...invoice,
            totalLicenses: invoiceData?.totalLicenses || 0,
            licenseSummary: invoiceData?.licenseSummary || {},
            billedLicenses: invoiceData?.licenses || [],
          };
        });
      } catch (error) {
        throw new Error("Failed to get invoices: " + error);
      }
    },
    async getSubscriptions() {
      try {
        const customerId = this.organizationBillingConfig.customerId;
        const subscriptionsSnapshot = await db
          .collection("customers")
          .doc(customerId)
          .collection("subscriptions")
          .get();

        const subscriptions = await Promise.all(
          subscriptionsSnapshot.docs.map(async (doc) => {
            const subscription = doc.data();
            let schedule = null;

            if (subscription.schedule) {
              const scheduleDoc = await db
                .collection("customers")
                .doc(customerId)
                .collection("subscription_schedules")
                .doc(subscription.schedule)
                .get();

              if (scheduleDoc.exists) {
                schedule = scheduleDoc.data();
              }
            }

            return {
              ...subscription,
              schedule,
            };
          }),
        );

        this.subscriptions = subscriptions;
      } catch (error) {
        throw new Error("Failed to get subscriptions: " + error);
      }
    },
    async getLicenses() {
      try {
        const customerId = this.organizationBillingConfig.customerId;
        const licensesSnapshot = await db
          .collection(`customers/${customerId}/licenses`)
          .get();
        this.licenses = licensesSnapshot.docs.map((doc) => {
          const data = doc.data();

          // Get the most recent invoice number from billingHistory
          let invoiceNumber = null;
          if (data.billingHistory && data.billingHistory.length > 0) {
            const latestBilling =
              data.billingHistory[data.billingHistory.length - 1];
            invoiceNumber = latestBilling.invoiceNumber || null;
          }

          // Create period range text for grouping
          let periodRange = "Unbekannt";
          if (data.periodStart) {
            const startStr = this.timestampConverter(data.periodStart);
            if (data.periodEnd) {
              periodRange = `${startStr} - ${this.timestampConverter(
                data.periodEnd,
              )}`;
            } else {
              periodRange = `${startStr} - Laufend`;
            }
          }

          return {
            ...data,
            isSelectable:
              data.status === "active" || data.status === "pendingCancellation",
            invoiceNumber,
            periodRange,
          };
        });
      } catch (error) {
        throw new Error("Failed to get licenses: " + error);
      }
    },
    async getProducts() {
      try {
        const productsSnapshot = await db
          .collection("products")
          .where("active", "==", true)
          .get();

        this.products = productsSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name,
            description: data.description,
            prices: data.prices || [],
            metadata: data.metadata || {},
          };
        });
      } catch (error) {
        throw new Error("Failed to get products: " + error);
      }
    },
    toggleCreateStripePortalSessionDialog() {
      this.createStripePortalSessionDialog.model =
        !this.createStripePortalSessionDialog.model;
    },
    createManualInvoice() {
      // ========================================
      // CONFIGURATION - Edit values in src/config/invoiceConfig.js
      // ========================================

      // Select environment config
      const config = USE_DEV_ENVIRONMENT ? ENV_CONFIG.dev : ENV_CONFIG.prod;

      // ========================================
      // IMPLEMENTATION - No need to edit below
      // ========================================

      // Helper function to parse DD.MM.YYYY date strings
      const parseGermanDate = (dateString) => {
        if (!dateString) return null;
        if (dateString instanceof Date) return dateString;

        const parts = dateString.split(".");
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
        return new Date(dateString);
      };

      // Build line items from configuration
      const lineItems = LINE_ITEMS_CONFIG.map((item) => {
        const product = config.products[item.productType];
        if (!product) {
          console.error(`Unknown product type: ${item.productType}`);
          return null;
        }

        // Use custom description if provided, otherwise use product default
        const description = item.description || product.description;

        // Use custom quantity if provided, otherwise use config quantity
        const quantity = item.quantity !== undefined ? item.quantity : 1;

        // Build base line item
        const lineItem = {
          description: description,
          product: product.productId,
          quantity: quantity,
          currency: INVOICE_CONFIG.currency,
          period: {
            start: parseGermanDate(item.periodStart),
            end: parseGermanDate(item.periodEnd),
          },
          metadata: {
            ...product.metadata,
          },
        };

        // Use custom total if provided, otherwise calculate from unitAmount
        if (item.total !== undefined) {
          // When using custom total, Stripe expects amount (total) instead of unitAmount
          lineItem.amount = item.total;
        } else {
          // Use unitAmount from product config
          lineItem.unitAmount = product.amount;
        }

        return lineItem;
      }).filter(Boolean);

      // Build payload
      const payload = {
        customerId: this.customer.id,
        lineItems: lineItems,
        autoAdvance: INVOICE_CONFIG.autoAdvance,
        accountTaxIds: config.accountTaxIds,
        organizationId: this.$route.params.organizationId,
        metadata: {
          organizationId: this.$route.params.organizationId,
          environment: USE_DEV_ENVIRONMENT ? "development" : "production",
        },
        renderingTemplateId: config.renderingTemplateId,
      };

      // Add optional description if specified
      if (INVOICE_CONFIG.description) {
        payload.description = INVOICE_CONFIG.description;
      }

      console.log(
        `Creating ${
          USE_DEV_ENVIRONMENT ? "DEV" : "PROD"
        } invoice with payload:`,
        payload,
      );

      const callFunction = functions.httpsCallable(
        "stripeBilling-createManualInvoice",
      );

      return callFunction(payload)
        .then((result) => {
          if (result.data && result.data.success) {
            const message = `✅ Invoice created successfully!\nID: ${
              result.data.invoiceId
            }\nEnvironment: ${
              USE_DEV_ENVIRONMENT ? "DEVELOPMENT" : "PRODUCTION"
            }`;
            alert(message);
            console.log("Invoice creation result:", result.data);
            // Reload invoices
            this.fetchAllData();
          }
        })
        .catch((error) => {
          console.error("Error creating manual invoice:", error);
          this.$store.commit("snackbar/setSnackbar", {
            type: "error",
            message: `Fehler beim Erstellen der Rechnung: ${error.message}`,
          });
        });
    },
    linkLicensesToInvoice() {
      if (!this.customer || !this.customer.id) {
        alert("Kundendaten nicht verfügbar");
        return;
      }

      // Load memberships from Vuex when opening dialog
      this.loadMemberships();

      // Open dialog for user-friendly input
      this.linkLicensesDialog.model = true;

      // Auto-select first line item if there's only one
      this.$nextTick(() => {
        if (this.availableLineItems.length === 1) {
          this.linkLicensesDialog.currentLicense.lineItemId =
            this.availableLineItems[0].id;
          this.onLineItemSelected();
        }
      });
    },
    async loadMemberships() {
      this.linkLicensesDialog.loadingMemberships = true;
      try {
        await this.$store.dispatch(`${ADMIN}${GET_USER_LIST}`, {
          organizationId: this.$route.params.organizationId,
        });
      } catch (error) {
        console.error("Error loading memberships:", error);
        this.$store.commit("snackbar/setSnackbar", {
          type: "error",
          message: "Fehler beim Laden der Mitglieder",
        });
      } finally {
        this.linkLicensesDialog.loadingMemberships = false;
      }
    },
    addLicensesToList() {
      const userIds = this.linkLicensesDialog.currentLicense.userIds;
      const lineItemId = this.linkLicensesDialog.currentLicense.lineItemId;
      const prorated = this.linkLicensesDialog.currentLicense.prorated;
      const sharedQuantity =
        this.linkLicensesDialog.currentLicense.sharedQuantity;
      const selectedLicenseId =
        this.linkLicensesDialog.currentLicense.selectedLicense;

      // Validate license selection for prorated items
      if (prorated && !selectedLicenseId) {
        this.$store.commit("snackbar/setSnackbar", {
          type: "error",
          message:
            "Bitte wählen Sie ein Lizenz/Produkt für anteilige Abrechnungen aus",
        });
        return;
      }

      if (userIds && userIds.length > 0 && lineItemId) {
        // Add each user as a separate license entry, but filter out any invalid userIds
        const validUserIds = userIds.filter(
          (userId) => userId && userId.trim() !== "",
        );

        if (validUserIds.length === 0) {
          console.error("No valid user IDs to add");
          return;
        }

        // Get license metadata if selected
        let licenseMetadata = null;
        if (selectedLicenseId) {
          const selectedProduct = this.products.find(
            (p) => p.id === selectedLicenseId,
          );
          if (selectedProduct && selectedProduct.prices.length > 0) {
            // Use the first active price
            const activePrice = selectedProduct.prices.find((p) => p.active);
            if (activePrice) {
              licenseMetadata = {
                title: selectedProduct.name,
                productId: selectedProduct.id,
                priceId: activePrice.id,
              };
            }
          }
        }

        validUserIds.forEach((userId) => {
          // Find user data from available members
          const userMember = this.availableMembers.find(
            (m) => m.userId === userId,
          );

          const licenseEntry = {
            userId: userId,
            lineItemId: lineItemId,
            prorated: prorated || false,
            sharedQuantity: prorated ? sharedQuantity || 1 : 1,
          };

          // Add user data if available to avoid backend lookup
          if (userMember) {
            licenseEntry.user = {
              uid: userId,
              displayName: userMember.displayName || "Unknown User",
              personnelNumber: userMember.personnelNumber || null,
            };
          }

          // Add license metadata if available
          if (licenseMetadata) {
            licenseEntry.license = licenseMetadata;
          }

          this.linkLicensesDialog.licenses.push(licenseEntry);
        });

        // Reset userIds and proration fields
        this.linkLicensesDialog.currentLicense.userIds = [];
        this.linkLicensesDialog.currentLicense.prorated = false;
        this.linkLicensesDialog.currentLicense.sharedQuantity = 1;
        // Don't reset selectedLicense to allow adding multiple batches with same product

        // Show success feedback
        this.$store.commit("snackbar/setSnackbar", {
          type: "success",
          message: `${validUserIds.length} Lizenz${
            validUserIds.length > 1 ? "en" : ""
          } hinzugefügt`,
        });

        // Focus back to user select for next batch
        this.$nextTick(() => {
          if (this.$refs.userSelect) {
            this.$refs.userSelect.focus();
          }
        });
      }
    },
    addLicensesToListManually() {
      // Manual add without auto-trigger
      this.addLicensesToList();
    },
    quickAddUsers() {
      // Quick add via Enter key
      if (this.linkLicensesDialog.currentLicense.userIds.length > 0) {
        this.addLicensesToList();
      }
    },
    onLineItemSelected() {
      // Clear any previously selected users when changing line item
      this.linkLicensesDialog.currentLicense.userIds = [];

      // Auto-fill proration fields based on line item data
      const lineItemId = this.linkLicensesDialog.currentLicense.lineItemId;
      if (lineItemId) {
        const lineItem = this.availableLineItems.find(
          (item) => item.id === lineItemId,
        );

        if (lineItem) {
          // Auto-detect proration from keywords
          if (lineItem.isProration) {
            this.linkLicensesDialog.currentLicense.prorated = true;

            // Try to auto-fill extracted quantity
            if (lineItem.extractedQuantity && lineItem.extractedQuantity > 0) {
              this.linkLicensesDialog.currentLicense.sharedQuantity =
                lineItem.extractedQuantity;

              // Show feedback
              this.$store.commit("snackbar/setSnackbar", {
                show: true,
                message: `Anteilige Abrechnung erkannt: ${
                  lineItem.extractedQuantity
                } Nutzer teilen sich ${this.getAmountFromCents(
                  lineItem.amount,
                  lineItem.currency,
                )}`,
                type: "info",
              });
            } else {
              this.linkLicensesDialog.currentLicense.sharedQuantity = 1;

              // Show feedback without quantity
              this.$store.commit("snackbar/setSnackbar", {
                show: true,
                message: `Anteilige Abrechnung erkannt. Bitte Anzahl der Nutzer eingeben.`,
                type: "info",
              });
            }
          } else {
            this.linkLicensesDialog.currentLicense.prorated = false;
            this.linkLicensesDialog.currentLicense.sharedQuantity = 1;
          }

          // Auto-select license/product based on line item description
          const description = (lineItem.description || "").toLowerCase();
          const matchedProduct = this.products.find((product) => {
            const productName = (product.name || "").toLowerCase();
            return description.includes(productName);
          });

          if (matchedProduct) {
            this.linkLicensesDialog.currentLicense.selectedLicense =
              matchedProduct.id;
            console.log(
              `Auto-selected license: ${matchedProduct.name} based on line item description`,
            );
          } else {
            // Clear selection if no match
            this.linkLicensesDialog.currentLicense.selectedLicense = null;
          }
        }
      } else {
        this.linkLicensesDialog.currentLicense.prorated = false;
        this.linkLicensesDialog.currentLicense.sharedQuantity = 1;
        this.linkLicensesDialog.currentLicense.selectedLicense = null;
      }

      // Auto-focus user select when line item is chosen
      this.$nextTick(() => {
        if (this.$refs.userSelect) {
          this.$refs.userSelect.focus();
        }
      });
    },
    removeUserFromSelection(index) {
      this.linkLicensesDialog.currentLicense.userIds.splice(index, 1);
    },
    getUserDisplayName(userId) {
      const member = this.availableMembers.find((m) => m.userId === userId);
      return member?.displayName || userId;
    },
    getUserCurrentLicense(userId) {
      const userLicenses = this.licenses.filter(
        (license) => license.userId === userId && license.status === "active",
      );
      if (userLicenses.length === 0) return null;
      if (userLicenses.length === 1) {
        return (
          userLicenses[0].name || userLicenses[0].productName || "Aktive Lizenz"
        );
      }
      return `${userLicenses.length} aktive Lizenzen`;
    },
    getLineItemDescription(lineItemId) {
      const lineItem = this.availableLineItems.find(
        (item) => item.id === lineItemId,
      );
      return lineItem?.description || lineItemId;
    },
    loadUsersFromReferenceLineItem() {
      if (!this.linkLicensesDialog.referenceLineItemId) return;

      // Find licenses that were billed in the reference line item
      const referenceLineItemId = this.linkLicensesDialog.referenceLineItemId;
      const referenceInvoiceId = this.linkLicensesDialog.referenceInvoiceId;

      // Get user IDs from licenses where billingHistory contains this line item
      const userIdsFromReference = this.licenses
        .filter((license) => {
          if (
            !license.billingHistory ||
            !Array.isArray(license.billingHistory)
          ) {
            return false;
          }
          // Check if this license has a billing entry for the reference invoice with matching line item
          return license.billingHistory.some((billing) => {
            return (
              billing.invoiceId === referenceInvoiceId &&
              billing.lineItemId === referenceLineItemId
            );
          });
        })
        .map((license) => license.userId || license.user?.uid)
        .filter(Boolean); // Remove any null/undefined values

      if (userIdsFromReference.length === 0) {
        this.$store.commit("snackbar/setSnackbar", {
          type: "info",
          message: "Keine Benutzer in der Referenz-Rechnung gefunden",
        });
        return;
      }

      // Add to current selection (avoiding duplicates)
      const currentUserIds =
        this.linkLicensesDialog.currentLicense.userIds || [];
      const combinedUserIds = [
        ...new Set([...currentUserIds, ...userIdsFromReference]),
      ];
      this.linkLicensesDialog.currentLicense.userIds = combinedUserIds;

      this.$store.commit("snackbar/setSnackbar", {
        type: "success",
        message: `${userIdsFromReference.length} Benutzer aus Referenz übernommen`,
      });
    },
    toggleUserSelection(userId) {
      if (!this.linkLicensesDialog.currentLicense.lineItemId) return;

      const index =
        this.linkLicensesDialog.currentLicense.userIds.indexOf(userId);
      if (index > -1) {
        this.linkLicensesDialog.currentLicense.userIds.splice(index, 1);
      } else {
        this.linkLicensesDialog.currentLicense.userIds.push(userId);
      }
    },
    removeLicenseFromList(index) {
      this.linkLicensesDialog.licenses.splice(index, 1);
    },
    closeLinkLicensesDialog() {
      this.linkLicensesDialog.model = false;
      this.linkLicensesDialog.invoiceId = "";
      this.linkLicensesDialog.licenses = [];
      this.linkLicensesDialog.currentLicense = {
        userIds: [],
        lineItemId: "",
        prorated: false,
        sharedQuantity: 1,
        selectedLicense: null,
      };
      this.linkLicensesDialog.errorMessage = "";
      this.linkLicensesDialog.userSearch = "";
      this.linkLicensesDialog.divisionFilter = null;
      this.linkLicensesDialog.referenceInvoiceId = "";
      this.linkLicensesDialog.referenceLineItemId = "";
    },
    async executeLinkLicensesToInvoice() {
      this.linkLicensesDialog.loading = true;
      this.linkLicensesDialog.errorMessage = "";

      const invoiceId = this.linkLicensesDialog.invoiceId;
      const licenses = this.linkLicensesDialog.licenses;

      try {
        const callFunction = functions.httpsCallable(
          "stripeBilling-linkLicensesToManualInvoice",
        );
        const payload = {
          customerId: this.customer.id,
          organizationId: this.$route.params.organizationId,
          invoiceId: invoiceId,
          licenses: licenses,
        };

        console.log("Sending payload:", payload);

        const result = await callFunction(payload);

        if (result.data.success) {
          console.log("✅ Success:", result.data);
          this.$store.commit("snackbar/setSnackbar", {
            type: "success",
            message: `Erfolgreich! ${result.data.licensesCreated} erstellt, ${result.data.licensesUpdated} aktualisiert. Bitte Seite neu laden.`,
          });
          this.closeLinkLicensesDialog();
          // Reload data
          await this.fetchAllData();
        } else {
          console.warn("Partial success:", result.data);
          this.linkLicensesDialog.errorMessage = `Teilweise erfolgreich: ${
            result.data.totalProcessed
          } verarbeitet, ${result.data.errors?.length || 0} Fehler`;
          if (result.data.errors) {
            console.error("Errors:", result.data.errors);
          }
        }
      } catch (error) {
        console.error("❌ Error:", error);
        this.linkLicensesDialog.errorMessage = error.message;
      } finally {
        this.linkLicensesDialog.loading = false;
      }
    },
    createSubscription() {
      const callFunction = functions.httpsCallable(
        "stripeBilling-createSubscription",
      );
      return callFunction({
        organizationId: this.$route.params.organizationId,
      })
        .then((result) => {
          if (result.data.success) {
            alert("success");
          }
        })
        .catch((error) => {
          // handle error
          console.log(error.data);
          console.error(error);
        });
    },
    createStripePortalSession() {
      this.createStripePortalSessionDialog.loading = true;

      if (!this.customer || !this.customer.id) {
        this.createStripePortalSessionDialog.errorMessage =
          "Kundendaten nicht verfügbar";
        this.createStripePortalSessionDialog.loading = false;
        return;
      }

      const callFunction = functions.httpsCallable(
        "stripeBilling-createStripePortalSession",
      );
      return callFunction({
        customerId: this.customer.id,
        returnUrl: window.location.href,
      })
        .then((result) => {
          if (result.data.success) {
            // this.showSuccess = true;
            this.createStripePortalSessionDialog.model = false;
            this.createStripePortalSessionDialog.loading = false;
            this.toggleCreateStripePortalSessionDialog();
            window.location.href = result.data.url;
          } else {
            this.createStripePortalSessionDialog.errorMessage =
              result.data.error.message;
            this.createStripePortalSessionDialog.loading = false;
          }
        })
        .catch((error) => {
          // handle error
          // console.error(err.errorMsg);
          console.error(error);
          // this.errorMsg = err.message;
          this.createStripePortalSessionDialog.errorMessage = error.message;
          this.createStripePortalSessionDialog.loading = false;
        });
    },
    getAmountFromCents(amount, currency) {
      return (
        (amount / 100).toFixed(2).replace(".", ",") + " " + currency
        // currency.toUpperCase()
      );
    },
    useExistingBillingAccount() {
      alert(
        "Um ein bestehendes Abrechnungskonto zu verwenden, müssen Sie sich an den Support wenden.\n\nGeben Sie dabei folgende Daten an:\n\nIhre existierende Kundenummer\nZu verknüpfende Organisations-ID: " +
          this.$route.params.organizationId,
      );
    },
    createNewBillingAccount() {
      alert(
        "Um ein neues Abrechnungskonto zu erstellen, müssen Sie sich an den Support wenden.\n\nGeben Sie dabei folgende Daten an:\n\nZu verknüpfende Organisations-ID: " +
          this.$route.params.organizationId,
      );
    },
    openInvoiceInBillingPortal(invoicePdfUrl) {
      window.open(invoicePdfUrl, "_blank");
    },
    timestampConverter(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
    copyToClipboard(text) {
      if (navigator.clipboard && text && text !== "N/A") {
        navigator.clipboard.writeText(text).catch((err) => {
          console.error("Failed to copy text: ", err);
        });
      }
    },
    filterLicensesByType(licenseType) {
      // Set the groupBy to license type and apply search filter
      this.licensesGroupBy = "license.title";
      this.licensesSearch = licenseType;
    },
    getBillingCycleText(cycle) {
      const cycles = {
        monthly: "Monat",
        yearly: "Jahr",
        quarterly: "Quartal",
        weekly: "Woche",
        daily: "Tag",
      };
      return cycles[cycle] || cycle;
    },
    getNextBillingClass(nextBillingDate) {
      // try {
      //   // Dynamic import of ExcelJS
      //   const ExcelJS = (await import("exceljs")).default;
      //   const workbook = new ExcelJS.Workbook();
      //   workbook.creator = "Mira";
      //   workbook.created = new Date();
      //   const worksheet = workbook.addWorksheet("Lizenzen");
      //   // Define columns
      //   worksheet.columns = [
      //     { header: "Lizenz-ID", key: "licenseId", width: 25 },
      //     { header: "Benutzer", key: "userName", width: 30 },
      //     { header: "Lizenztyp", key: "licenseType", width: 20 },
      //     { header: "Status", key: "status", width: 15 },
      //     { header: "Produktname", key: "productName", width: 25 },
      //     { header: "Preis-ID", key: "priceId", width: 25 },
      //     { header: "Kosten (Cents)", key: "cost", width: 15 },
      //     { header: "Währung", key: "currency", width: 10 },
      //     { header: "Startdatum", key: "periodStart", width: 15 },
      //     { header: "Enddatum", key: "periodEnd", width: 15 },
      //     { header: "Rechnung", key: "invoiceNumber", width: 20 },
      //     { header: "Abrechnungsintervall", key: "billingInterval", width: 20 },
      //   ];
      //   // Style header row
      //   worksheet.getRow(1).font = { bold: true };
      //   worksheet.getRow(1).fill = {
      //     type: "pattern",
      //     pattern: "solid",
      //     fgColor: { argb: "FF1976D2" },
      //   };
      //   worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      //   // Add data
      //   this.licenses.forEach((license) => {
      //     worksheet.addRow({
      //       licenseId: license.licenseId || "N/A",
      //       userName: license.user?.displayName || "N/A",
      //       licenseType: license.license?.title || license.name || "N/A",
      //       status: license.status || "N/A",
      //       productName: license.productId || "N/A",
      //       priceId: license.priceId || "N/A",
      //       cost: license.priceUnitAmount || 0,
      //       currency: (license.currency || "eur").toUpperCase(),
      //       periodStart: license.periodStart
      //         ? this.timestampConverter(license.periodStart)
      //         : "N/A",
      //       periodEnd: license.periodEnd
      //         ? this.timestampConverter(license.periodEnd)
      //         : "Laufend",
      //       invoiceNumber: license.invoiceNumber || "N/A",
      //       billingInterval: license.price?.recurring?.interval || "N/A",
      //     });
      //   });
      //   // Auto-fit columns (approximate)
      //   worksheet.columns.forEach((column) => {
      //     const maxLength = column.values.reduce((max, val) => {
      //       const length = val ? val.toString().length : 0;
      //       return Math.max(max, length);
      //     }, 10);
      //     column.width = Math.min(maxLength + 2, 50);
      //   });
      //   // Generate Excel file
      //   const buffer = await workbook.xlsx.writeBuffer();
      //   const blob = new Blob([buffer], {
      //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      //   });
      //   // Download file
      //   const link = document.createElement("a");
      //   link.href = URL.createObjectURL(blob);
      //   const date = new Date().toISOString().slice(0, 10);
      //   link.download = `lizenzen-export-${date}.xlsx`;
      //   link.click();
      //   this.$store.commit("showSnackbar", {
      //     message: `${this.licenses.length} Lizenzen erfolgreich exportiert`,
      //     color: "success",
      //   });
      // } catch (error) {
      //   console.error("Error exporting to Excel:", error);
      //   this.$store.commit("showSnackbar", {
      //     message: "Fehler beim Exportieren: " + error.message,
      //     color: "error",
      //   });
      // }
      console.log(nextBillingDate);
    },
    getProrationReasonText(reason) {
      const reasons = {
        started_mid_period: "Lizenz während Periode gestartet",
        ended_mid_period: "Lizenz während Periode beendet",
        full_period: "Vollständige Periode",
        partial_usage: "Teilweise Nutzung",
      };
      return reasons[reason] || reason;
    },
    getInvoiceById(invoiceId) {
      return this.invoices.find((inv) => inv.id === invoiceId) || null;
    },
    getGroupHeaderText(item) {
      // Get the displayed value based on current grouping
      const groupBy = this.licensesGroupBy;

      if (groupBy === "user.displayName") {
        return `${item.user?.firstName || ""} ${item.user?.lastName || ""}`;
      } else if (groupBy === "status") {
        return item.status || "N/A";
      } else if (groupBy === "license.title") {
        return item.license?.title || "N/A";
      } else if (groupBy === "invoiceNumber") {
        return item.invoiceNumber || "Keine Rechnung";
      } else if (groupBy === "periodRange") {
        return item.periodRange || "Unbekannt";
      }

      return "Unbekannt";
    },
    async reconcileLicenseInvoices() {
      this.reconcilingLicenses = true;
      try {
        const reconcileFunction = this.$firebase.functions.httpsCallable(
          "scheduled-reconcileCustomerLicenses",
        );

        const result = await reconcileFunction({
          customerId: this.customer?.stripeCustomerId,
        });

        if (result.data.success) {
          this.$store.commit("showSnackbar", {
            message: `Abgleich abgeschlossen: ${result.data.licensesLinked} Lizenzen mit Rechnungen verknüpft. Bitte Seite neu laden.`,
            color: "success",
          });
        } else {
          this.$store.commit("showSnackbar", {
            message:
              "Abgleich mit Fehlern abgeschlossen. Siehe Console für Details.",
            color: "warning",
          });
          console.warn("Reconciliation errors:", result.data.errors);
        }
      } catch (error) {
        console.error("Error reconciling licenses:", error);
        this.$store.commit("showSnackbar", {
          message: "Fehler beim Abgleich der Lizenzen: " + error.message,
          color: "error",
        });
      } finally {
        this.reconcilingLicenses = false;
      }
    },
    /**
     * Link licenses to a manual invoice.
     * Call this from browser console after creating a manual invoice.
     *
     * Example usage:
     * vm.linkLicensesToManualInvoice({
     *   invoiceId: "in_xxxxx",
     *   licenses: [
     *     { userId: "user1", priceId: "price_standard", periodStart: "2024-11-01", periodEnd: "2024-12-01", status: "canceled" },
     *     { userId: "user2", priceId: "price_pro", periodStart: "2024-11-01", periodEnd: null, status: "active" }
     *   ]
     * })
     */
    async linkLicensesToManualInvoice({ invoiceId, licenses }) {
      if (!invoiceId || !licenses || !Array.isArray(licenses)) {
        console.error(
          "Invalid parameters. Required: invoiceId (string) and licenses (array)",
        );
        return;
      }

      if (!this.customer || !this.customer.id) {
        console.error("Customer data not available");
        return;
      }

      console.log(
        `Linking ${licenses.length} licenses to invoice ${invoiceId}...`,
      );

      try {
        const linkFunction = functions.httpsCallable(
          "stripeBilling-linkLicensesToManualInvoice",
        );

        const result = await linkFunction({
          customerId: this.customer.id,
          organizationId: this.$route.params.organizationId,
          invoiceId: invoiceId,
          licenses: licenses,
        });

        if (result.data.success) {
          console.log("✅ Success!", result.data);
          console.log(`Created ${result.data.licensesCreated} licenses:`);
          result.data.licenses.forEach((lic) => {
            console.log(
              `  - ${lic.userName} (${lic.productName}): ${lic.licenseId}`,
            );
          });

          alert(
            `Erfolgreich ${result.data.licensesCreated} Lizenzen erstellt und mit Rechnung ${invoiceId} verknüpft! Bitte Seite neu laden.`,
          );
        } else {
          console.error("❌ Failed:", result.data);
          alert("Fehler beim Verknüpfen der Lizenzen. Siehe Console.");
        }
      } catch (error) {
        console.error("❌ Error linking licenses:", error);
        alert(`Fehler: ${error.message}`);
      }
    },
  },
};
</script>
